import { NO_RISK } from '@zefr/style-guide/dist/ui/types/atrium/garm';
import { atom, Getter, Setter } from 'jotai';
import { atomWithHash } from 'jotai-location';
import { compressToEncodedURIComponent, decompressFromEncodedURIComponent } from 'lz-string';
import { INCLUSION_LIST_FORM, INCLUSION_LIST_PREFAB } from '../constants/listTypes';
import contentAlignmentOptionsFormAtom, {
  contentAlignmentOptionsAtom,
} from './contentAlignmentAtom';
import targetAudienceOptionsFormAtom, { targetAudienceOptionsAtom } from './targetAudienceAtom';

// Populate this with the data that can be filled in by picking a prefab
export interface PrefabData {
  targetAudience: Record<string, unknown>;
  contentAlignment: Record<string, unknown>;
  includedConcepts: string[];
  excludedConcepts: string[];
  creativeAlignment: string;
  risk: string;
  languages: string[];
  countries?: string[];
  threshold?: number;
}

const defaultPrefabData: PrefabData = {
  targetAudience: {},
  contentAlignment: {},
  includedConcepts: [],
  excludedConcepts: [],
  creativeAlignment: '',
  risk: NO_RISK,
  languages: [],
  // Country Popularity is the default Primary Sort, which requires at least
  // one country selected. Pre-fill with the United States so a brand-new
  // session lands on results immediately instead of an "at least one
  // country required" validation error. The user can still clear it to
  // an explicit empty array; that intent is respected downstream.
  countries: ['United States'],
  threshold: undefined,
};

export const SORT_MODE_AVG_DAILY_VIEWS = 'avg_daily_views';
export const SORT_MODE_COUNTRY_POPULARITY = 'country_popularity';
export type SortMode = typeof SORT_MODE_AVG_DAILY_VIEWS | typeof SORT_MODE_COUNTRY_POPULARITY;

export interface KnownFormData extends Partial<PrefabData> {
  platform?: string | null;
  type?: string | null;
  exclusions?: string[];
  step?: number;
  concepts?: string[];
  kpiKeys?: string[];
  inclusionListType?: string;
  prefab?: string;
  sortMode?: SortMode;
}

interface FormData extends KnownFormData {
  [x: string]: unknown;
}
const FORM_STATE_KEY = 'state';

const hashAtom = atomWithHash<FormData>(
  FORM_STATE_KEY,
  {},
  {
    serialize: (form) => {
      return compressToEncodedURIComponent(JSON.stringify(form));
    },
    deserialize: (currentEncodedString) => {
      const decodedJsonBlobAsString = decompressFromEncodedURIComponent(currentEncodedString || '');
      let decodedJsonData: FormData = {};
      try {
        if (decodedJsonBlobAsString) {
          decodedJsonData = JSON.parse(decodedJsonBlobAsString);
        }
      } catch (e) {
        console.error('Malformed state', e, decodedJsonBlobAsString);
      }
      return decodedJsonData;
    },
  },
);

const formDataAtom = atom((get) => {
  return get(hashAtom);
});

formDataAtom.debugLabel = 'Form Window State Decoded';

const formParamsAtom = atom((get) => {
  const formData = get(formDataAtom);
  let step = Number.isNaN(formData.step) ? 1 : formData.step || 1;
  if (step < 1) {
    step = 1;
  }
  let nextStep = 1;
  if (formData.platform) {
    nextStep = 2;
    if (formData.kpiKeys) {
      nextStep = 3;
      if (formData.type) {
        nextStep = 4;
        if (formData.type === 'inclusion') {
          nextStep = 5;
          if (formData.inclusionListType) {
            if (formData.prefab || formData.risk) {
              nextStep = 6;
            }
          }
        } else if (formData.type === 'exclusion') {
          nextStep = 5;
          if (formData.risk) {
            nextStep = 6;
          }
        }
      }
    }
  }
  // If we don't have a parameter, force the form to go to the active step
  if (Number.isNaN(formData.step) && nextStep !== 1) {
    step = nextStep - 1;
  }
  if (step > nextStep) {
    step = Math.min(nextStep, step);
  }

  return {
    nextStep,
    targetAudience: formData.targetAudience || {},
    contentAlignment: formData.contentAlignment || {},
    ...formData,
    step,
  };
});

formParamsAtom.debugLabel = 'Form params';

const setEncodedLocation = (set: Setter, value: FormData) => {
  set(hashAtom, value);
};

const setFormKeyValue: <T>(get: Getter, set: Setter, key: string, value: T) => void = (
  get,
  set,
  key,
  value,
) => {
  if (value) {
    if ((key as keyof KnownFormData) === 'contentAlignment') {
      const options = get(contentAlignmentOptionsAtom);
      Object.keys(options).forEach((optKey) => {
        const data = options[optKey];
        const currentValue = (value as Record<string, unknown>)[optKey];
        if (!currentValue) {
          if (data.defaultValue) {
            (value as Record<string, unknown>)[optKey] = data.defaultValue;
          }
        }
      });
      set(contentAlignmentOptionsFormAtom, value as Record<string, string | string[] | number>);
    } else if ((key as keyof KnownFormData) === 'targetAudience') {
      const options = get(targetAudienceOptionsAtom);
      Object.keys(options).forEach((optKey) => {
        const data = options[optKey];
        const currentValue = (value as Record<string, unknown>)[optKey];
        if (!currentValue) {
          if (data.defaultValue) {
            (value as Record<string, unknown>)[optKey] = data.defaultValue;
          }
        }
      });
      set(targetAudienceOptionsFormAtom, value as Record<string, string | string[] | number>);
    }
  }
  const previousData = get(formDataAtom);
  if (value) {
    previousData[key] = value;
  } else {
    delete previousData[key];
  }
  setEncodedLocation(set, previousData);
};

const setFormConceptsAtom = atom(null, (get, set, value: string[]) => {
  const key: keyof KnownFormData = 'concepts';
  setFormKeyValue(get, set, key, value);
});

const setFormPlatformAtom = atom(null, (get, set, value: string) => {
  const key: keyof KnownFormData = 'platform';
  setFormKeyValue(get, set, key, value);
});

const setFormListAtom = atom(null, (get, set, value: string) => {
  const key: keyof KnownFormData = 'type';
  setFormKeyValue(get, set, key, value);
  const keysToClear: (keyof KnownFormData)[] = [
    'includedConcepts',
    'excludedConcepts',
    'inclusionListType',
    'contentAlignment',
    'creativeAlignment',
    'prefab',
    'risk',
    'targetAudience',
    'exclusions',
    'languages',
    'countries',
    'threshold',
    'sortMode',
  ];
  keysToClear.forEach((k) => {
    setFormKeyValue(get, set, k, undefined);
  });
});

const setFormRiskAtom = atom(null, (get, set, value: string) => {
  const key: keyof KnownFormData = 'risk';
  setFormKeyValue(get, set, key, value);
});

const setFormThresholdAtom = atom(null, (get, set, value?: number) => {
  const key: keyof KnownFormData = 'threshold';
  setFormKeyValue(get, set, key, value);
});

const setFormLanguagesAtom = atom(null, (get, set, value: string[]) => {
  const key: keyof KnownFormData = 'languages';
  setFormKeyValue(get, set, key, value);
});

const setFormCountriesAtom = atom(null, (get, set, value: string[]) => {
  const key: keyof KnownFormData = 'countries';
  setFormKeyValue(get, set, key, value);
});

const setFormSortModeAtom = atom(null, (get, set, value: SortMode) => {
  const key: keyof KnownFormData = 'sortMode';
  setFormKeyValue(get, set, key, value);
});

const setFormPrefabAtom = atom(
  null,
  (get, set, value: string, data: PrefabData = defaultPrefabData) => {
    const key: keyof KnownFormData = 'prefab';
    const previousData = get(formDataAtom);

    // Iterate the full set of prefab keys (not just the keys present on `data`)
    // so optional fields like `countries` and `threshold` get reset to their
    // defaults when a new prefab payload omits them. Otherwise, the previous
    // prefab's value would leak through.
    (Object.keys(defaultPrefabData) as (keyof PrefabData)[]).forEach((prefabKey) => {
      const formKey = prefabKey as keyof KnownFormData;
      const nextValue = data[prefabKey] ?? defaultPrefabData[prefabKey];
      setFormKeyValue(get, set, formKey, nextValue);
    });
    if (previousData.inclusionListType === INCLUSION_LIST_PREFAB && value) {
      const inclusionListTypeKey: keyof KnownFormData = 'inclusionListType';
      setFormKeyValue(get, set, inclusionListTypeKey, INCLUSION_LIST_FORM);
    }
    setFormKeyValue(get, set, key, value);
  },
);

const setFormIncludedConceptsAtom = atom(null, (get, set, value: string[]) => {
  const key: keyof KnownFormData = 'includedConcepts';
  setFormKeyValue(get, set, key, value);
});

const setFormExcludedConceptsAtom = atom(null, (get, set, value: string[]) => {
  const key: keyof KnownFormData = 'excludedConcepts';
  setFormKeyValue(get, set, key, value);
});

const setFormExclusionsAtom = atom(null, (get, set, value: string[]) => {
  const key: keyof KnownFormData = 'exclusions';
  setFormKeyValue(get, set, key, value);
});

const setFormInclusionListTypeAtom = atom(null, (get, set, value: string) => {
  const key: keyof KnownFormData = 'inclusionListType';
  if (value === INCLUSION_LIST_PREFAB) {
    const prefabkey: keyof KnownFormData = 'prefab';
    setFormKeyValue(get, set, prefabkey, undefined);
  }
  setFormKeyValue(get, set, key, value);
});

const setFormStepOverrideAtom = atom(null, (get, set, value: number) => {
  const key: keyof KnownFormData = 'step';
  setFormKeyValue(get, set, key, value);
});

const setFormKpiKeysAtom = atom(null, (get, set, value: string[]) => {
  const key: keyof KnownFormData = 'kpiKeys';
  setFormKeyValue(get, set, key, value);
});

const setFormCreativeAlignmentAtom = atom(null, (get, set, value: string) => {
  const key: keyof KnownFormData = 'creativeAlignment';
  setFormKeyValue(get, set, key, value);
});

const setFormTargetAudienceAtom = atom(null, (get, set, value: Record<string, unknown>) => {
  const key: keyof KnownFormData = 'targetAudience';
  setFormKeyValue(get, set, key, value);
});

const setFormContentAlignmentAtom = atom(null, (get, set, value: Record<string, unknown>) => {
  const key: keyof KnownFormData = 'contentAlignment';
  // const options = get(contentAlignmentOptionsAtom);
  // Object.keys(options).forEach((optKey) => {
  //   const data = options[optKey];
  //   const currentValue = value[key];
  //   if (!currentValue) {
  //     if (data.defaultValue) {
  //       value[key] = data.defaultValue;
  //     }
  //   }
  // });
  setFormKeyValue(get, set, key, value);
});

const privateAtoms = [
  setFormPlatformAtom,
  setFormStepOverrideAtom,
  setFormListAtom,
  setFormExclusionsAtom,
  setFormRiskAtom,
  setFormConceptsAtom,
  setFormKpiKeysAtom,
  setFormTargetAudienceAtom,
  setFormContentAlignmentAtom,
  setFormIncludedConceptsAtom,
  setFormExcludedConceptsAtom,
  setFormCreativeAlignmentAtom,
  setFormPrefabAtom,
  setFormInclusionListTypeAtom,
  setFormLanguagesAtom,
  setFormCountriesAtom,
  setFormThresholdAtom,
  setFormSortModeAtom,
];

privateAtoms.forEach((a) => {
  a.debugPrivate = true;
});

export {
  formParamsAtom,
  hashAtom,
  setFormConceptsAtom,
  setFormContentAlignmentAtom,
  setFormCountriesAtom,
  setFormCreativeAlignmentAtom,
  setFormExcludedConceptsAtom,
  setFormExclusionsAtom,
  setFormIncludedConceptsAtom,
  setFormInclusionListTypeAtom,
  setFormKpiKeysAtom,
  setFormLanguagesAtom,
  setFormListAtom,
  setFormPlatformAtom,
  setFormPrefabAtom,
  setFormRiskAtom,
  setFormSortModeAtom,
  setFormStepOverrideAtom,
  setFormTargetAudienceAtom,
  setFormThresholdAtom,
};
